import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { SupportedLanguage } from '../types';

interface LanguageTabProps {
  supportedLanguages: SupportedLanguage[];
  currentLanguage: string;
  onSelectLanguage: (code: string) => void;
  // CF suggestion props
  cfLanguage?: string | null;
  showCfSuggestion?: boolean;
  onDismissSuggestion?: () => void;
}

export function LanguageTab({
  supportedLanguages,
  currentLanguage,
  onSelectLanguage,
  cfLanguage,
  showCfSuggestion = false,
  onDismissSuggestion,
}: LanguageTabProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if we have a valid CF suggestion
  const cfLang = cfLanguage ? supportedLanguages.find(l => l.code === cfLanguage) : null;
  const hasCfSuggestion = showCfSuggestion && cfLang && cfLanguage !== currentLanguage;

  // Show CF language code when suggesting, otherwise current language
  const displayCode = hasCfSuggestion
    ? cfLanguage!.toUpperCase()
    : currentLanguage.toUpperCase();

  // Calculate dropdown position when opening
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      updatePosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    // If there's a CF suggestion, accept it on single click
    if (hasCfSuggestion && cfLanguage) {
      onSelectLanguage(cfLanguage);
      onDismissSuggestion?.();
      return;
    }

    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);

    // Opening the dropdown dismisses any suggestion (user engaged)
    if (!isOpen) {
      onDismissSuggestion?.();
    }
  };

  const handleSelect = (code: string) => {
    onSelectLanguage(code);
    onDismissSuggestion?.(); // Dismiss suggestion when user makes explicit choice
    setIsOpen(false);
  };

  // Render dropdown in a portal to escape overflow:hidden containers
  const dropdown = isOpen ? createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl min-w-[200px] py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
      style={{
        top: dropdownPosition.top,
        right: dropdownPosition.right,
      }}
    >
      {supportedLanguages.map((lang) => {
        const isActive = lang.code === currentLanguage;
        return (
          <button
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5
              text-left transition-colors
              ${isActive
                ? 'bg-blue-50 text-blue-600'
                : 'hover:bg-gray-50 text-gray-700'
              }
            `}
          >
            <span className={`
              text-[11px] font-bold uppercase tracking-wide min-w-[24px]
              ${isActive ? 'text-blue-600' : 'text-gray-500'}
            `}>
              {lang.code.toUpperCase()}
            </span>
            <span className="text-sm font-medium flex-1">
              {lang.name}
            </span>
            {isActive && (
              <span className="text-blue-600 text-sm">✓</span>
            )}
          </button>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative flex-shrink-0">
      {/* Language Tab Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`
          h-12 px-3 min-w-[60px]
          flex items-center justify-center gap-1.5
          border-b-2 transition-all duration-200
          ${isOpen
            ? 'border-blue-600 bg-blue-50 text-blue-700'
            : hasCfSuggestion
              ? 'border-blue-500 bg-blue-100 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }
          ${(hasCfSuggestion && !isOpen) ? 'pulse-language' : ''}
        `}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-xs font-semibold tracking-wide">
          {displayCode}
        </span>
        <span
          className={`text-[9px] text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          ▾
        </span>
      </button>

      {dropdown}
    </div>
  );
}
